import { style } from 'typestyle';
import { StaticDashboard, type ParameterValues } from '@metabase/embedding-sdk-react';
import { ADALONG_COLORS } from '@src/styles';
import { AnalyzeMetabaseProvider } from './metabaseProvider';

interface Props {
    dashboardId?: number;
    token?: string;
    parameters?: Record<string, unknown>;
    selectionKey?: string;
    title: string;
    minHeight?: number;
    fillHeight?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    loadingMessage?: string;
    errorMessage?: string;
    unavailableMessage?: string;
}

/**
 * Mounts a Metabase dashboard through the Modular Embedding SDK.
 *
 * Render order (highest priority first):
 *   1. loading     – React Query `isLoading`; the SDK is not mounted yet.
 *   2. error       – React Query `isError`; the SDK is not mounted.
 *   3. unavailable – the dashboard ID or Guest token is missing.
 *   4. dashboard   – the `StaticDashboard` inside the Guest `MetabaseProvider`.
 */
export function MetabaseSdkDashboard({
    dashboardId,
    token,
    parameters,
    selectionKey,
    title,
    minHeight = 400,
    fillHeight = false,
    isLoading = false,
    isError = false,
    loadingMessage = 'Loading dashboard…',
    errorMessage = 'Unable to load the Metabase dashboard. Please check your connection.',
    unavailableMessage = 'Dashboard is not available yet.',
}: Props) {
    const cardStyle = {
        boxSizing: 'border-box',
        width: '100%',
        minHeight,
        height: fillHeight ? '100%' : 'auto',
    };

    if (isLoading) {
        return (
            <section className={dashboardClass} style={cardStyle} aria-label={title}>
                <div role="status" className={stateClass} aria-live="polite">
                    {loadingMessage}
                </div>
            </section>
        );
    }

    if (isError) {
        return (
            <section className={dashboardClass} style={cardStyle} aria-label={title}>
                <div role="alert" className={stateClass} aria-live="assertive">
                    {errorMessage}
                </div>
            </section>
        );
    }

    if (dashboardId === undefined || dashboardId === null || dashboardId === 0 || !token) {
        return (
            <section className={dashboardClass} style={cardStyle} aria-label={title}>
                <div role="status" className={stateClass} aria-live="polite">
                    {unavailableMessage}
                </div>
            </section>
        );
    }

    // The key forces a full remount of the SDK when switching between Cloud
    // dashboards or semantic period selections. Combined with the AnalyzePage
    // single-mount conditional (ecommerce XOR ugc panel), this keeps a previous
    // dashboard from being displayed under a new period label.
    //
    // `withDownloads={false}` preserves the historical (pre-SDK) behavior, where
    // dashboards were rendered without Metabase's download actions.
    const editableParameters = Object.fromEntries(
        Object.entries(parameters ?? {}).filter(([name]) => ![
            'group_name',
            'date',
            'unit',
            'timeframe',
            'period_unit',
            'kpi_period',
            'chart_period',
            'time_grouping',
        ].includes(name)),
    ) as ParameterValues;
    const dashboardKey = `${dashboardId}:${selectionKey ?? ''}:${JSON.stringify(editableParameters)}`;

    return (
        <section className={dashboardClass} style={cardStyle} aria-label={title}>
            <AnalyzeMetabaseProvider>
                <StaticDashboard
                    key={dashboardKey}
                    className={dashboardClass}
                    token={token}
                    // The backend contract is `Record<string, unknown>` while the SDK
                    // accepts scalar/array parameter values. The backend only sends
                    // strings here, so the boundary cast is safe. Group and period
                    // parameters are deliberately filtered because they are locked
                    // inside the JWT.
                    parameters={editableParameters}
                    withTitle={false}
                    withCardTitle
                    withDownloads={false}
                    withSubscriptions={false}
                    style={cardStyle}
                />
            </AnalyzeMetabaseProvider>
        </section>
    );
}

const dashboardClass = style({
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    $nest: {
        // StaticDashboard keeps an empty header (and Commerce adds a top rule)
        // even when its title is hidden. Scope this to the embed instance.
        '& header': {
            display: 'none',
        },
        // This one-level Treemap renders the raw measure and its percentage as
        // separate SVG labels. Keep the percentage and device label while
        // hiding the raw measure and the chart's "Total" breadcrumb.
        '& [data-viz-ui-name="Treemap"] [data-testid="chart-container"] svg text[style*="font-size: 20px"][style*="font-weight: 700"]': {
            display: 'none',
        },
        '& [data-viz-ui-name="Treemap"] [data-testid="chart-container"] svg text[style*="font-weight: 400"]': {
            fontSize: '24px !important',
            fontWeight: '700 !important',
            // Preserve Metabase's per-cell SVG transform while shifting the
            // percentage label above the hidden raw measure.
            translate: '0 -20px !important',
        },
        '& [data-viz-ui-name="Treemap"] [data-testid="treemap-breadcrumb"]': {
            display: 'none',
        },
    },
});

const stateClass = style({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: 24,
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 14,
    textAlign: 'center',
});
